/*
 * zeroLabs Logo mit Augen.
 * Oben steht der volle Schriftzug. Beim Scrollen schiebt er sich zur 0,0 zusammen,
 * beim Zurückscrollen faltet er sich mit etwas Feder wieder aus.
 * Die Augen folgen Maus und Finger. Die Neigung am Handy ist bewusst weggelassen (Moritz, 30.09.2026).
 *
 * Bewegungsfamilie Physik: gedämpfte Feder. Startwerte kommen aus zl-tokens.css
 * (Abschnitt "Logo und Augen"), im Labor mit Reglern veränderbar.
 * Bei reduzierter Bewegung: kein Übergang, die Augen bleiben ruhig.
 *
 * Markup: snippets/zl-logo.liquid. Einstellungen: window.zlLogo.settings.
 */
(() => {
  const rootStyle = () => getComputedStyle(document.documentElement);
  const str = (style, name, fallback, allowed) => {
    const v = style.getPropertyValue(name).trim().replace(/^['"]|['"]$/g, '');
    return allowed.includes(v) ? v : fallback;
  };
  const num = (style, name, fallback) => {
    const v = parseFloat(style.getPropertyValue(name));
    return Number.isFinite(v) ? v : fallback;
  };

  // Maße im Koordinatensystem des Schriftzugs (Agentur-Datei, Einheit = viewBox)
  const BASE_WORD = 19.43;          // Grundlinie Schriftzug
  const WORD_W = 119.93;            // Breite Schriftzug
  const SLOT = 59.765;              // Schlitz zwischen "zero" und "Labs" (rechte Kante o 59,02, linke Kante L 60,51)
  const SIGN = {
    width: 37.936,
    tinte: 0.852,                   // sichtbarer Anteil der Breite: die Nullen haben im Kasten links und rechts je rund 7 % Luft (gemessen 40,9 von 48 px)
    base: 19.92,                    // Grundlinie Zeichen (Unterkante Null minus Überhang)
    meet: 18.999,                   // Mitte des Punkts: hier treffen sich die Nullen beim Auftauchen
    rest: { x: -2.357, y: 0.85 },   // Blick im Poster: nach links unten
    travel: { x: 3, y: 5 },         // Spielraum der Pupille bei Blickweite 1: am Rand verschmilzt sie mit der Null, wie im Poster
  };

  const defaults = () => {
    const s = rootStyle();
    return {
      modus: 'schwelle',                                  // 'schwelle' oder 'mitlaufen'
      ausfalten: 'oben',                                  // 'oben' oder 'hoch' (bei jeder Aufwärtsbewegung)
      schwelle: num(s, '--zl-logo-schwelle', 240),         // px Scrollweg bis zum Zusammenschieben
      strecke: num(s, '--zl-logo-strecke', 120),          // px Scrollweg beim Mitlaufen
      zuDauer: num(s, '--zl-logo-zu-dauer', 0.95),         // s bis der Schriftzug zur 0,0 zusammengeschoben ist
      zuSchwingen: num(s, '--zl-logo-zu-schwingen', 0),   // Anteil, um den die Bewegung über das Ziel hinausschwingt
      aufDauer: num(s, '--zl-logo-auf-dauer', 0.95),      // s bis der Schriftzug wieder ausgefaltet ist
      aufSchwingen: num(s, '--zl-logo-auf-schwingen', 0.08),
      weich: num(s, '--zl-logo-weich', 0.5),              // 0 = gleichmäßig, 1 = sanfter Anfang und sanftes Ende
      uebergabe: num(s, '--zl-logo-uebergabe', 0.3),      // 0 = erst Buchstaben weg, dann Nullen; höher = beides gleichzeitig
      uebergang: str(s, '--zl-logo-uebergang', 'schlitz', ['schlitz', 'o', 'lider', 'kronkorken', 'kohlensaeure']), // Art des Übergangs Schriftzug ↔ 0,0
      weg: str(s, '--zl-logo-weg', 'schlitz', ['schlitz', 'vorfahren', 'rollen', 'blick']), // Weg der 0,0 zur Mitte des Kopfs (Desktop): 'schlitz', 'vorfahren', 'rollen', 'blick'
      zeichen: num(s, '--zl-logo-zeichen', 1),            // Größe der 0,0, 1 = Null so hoch wie das L
      blick: num(s, '--zl-auge-blick', 1),                // Blickweite der Pupillen
      augeDauer: num(s, '--zl-auge-dauer', 0.3),          // s bis die Pupille dem Finger oder der Maus gefolgt ist
      augeSchwingen: num(s, '--zl-auge-schwingen', 0.02),
      maus: str(s, '--zl-auge-maus', 'bildschirm', ['bildschirm', 'winkel']), // Desktop: 'bildschirm' = Mitte des Bildschirms ist geradeaus, 'winkel' = genau zur Maus
      reichweite: num(s, '--zl-auge-reichweite', 220),    // px: so weit weg schaut die Pupille bis zum Rand (Maus, Finger nach Position)
      finger: str(s, '--zl-auge-finger', 'natuerlich', ['scroll', 'natuerlich', 'wisch', 'position']), // Handy: 'natuerlich' = Daumen und Schwung (Moritz, 10.10.2026), 'scroll' = Scrollrichtung, in Ruhe geradeaus, 'wisch' = Wischrichtung, 'position' = Ort des Fingers
      augeDauerHandy: num(s, '--zl-auge-dauer-handy', 0.3), // s: Folgezeit am Handy
      empfindlich: num(s, '--zl-auge-empfindlich', 0.31), // Handy: 0 = träge, 1 = sehr empfindlich. Steuert Wischweg und Schwung gemeinsam
      ruhe: num(s, '--zl-auge-ruhe', 4),                  // s ohne Mausbewegung, dann zurück zum Poster-Blick (0 = nie). Nicht am Handy
      immerZeichen: false,                                // Labor: Zeichen dauerhaft zeigen
      reduziert: false,                                   // Labor: reduzierte Bewegung nachstellen
    };
  };

  const settings = defaults();
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const smooth = (t) => t * t * (3 - 2 * t);
  const smoother = (t) => t * t * t * (t * (t * 6 - 15) + 10);
  const lerp = (a, b, t) => a + (b - a) * t;
  // Feder aus zwei verständlichen Werten: Dauer (bis 95 % des Wegs erreicht sind) und Nachschwingen (Anteil über das Ziel hinaus).
  // Daraus Steifigkeit und Dämpfung bei Masse 1. Die Zeit bis 95 % wird für den Dämpfungsgrad einmal nachgerechnet.
  const t95cache = new Map();
  const t95 = (z) => {
    const key = z.toFixed(3);
    if (t95cache.has(key)) return t95cache.get(key);
    let x = 0, v = 0, t = 0; const dt = 1 / 1000;
    while (x < 0.95 && t < 20) { v += (-(x - 1) - 2 * z * v) * dt; x += v * dt; t += dt; }
    t95cache.set(key, t); return t;
  };
  const spring = (dauer, schwingen) => {
    const os = clamp(schwingen, 0, 0.6);
    const z = os < 0.001 ? 1 : -Math.log(os) / Math.sqrt(Math.PI ** 2 + Math.log(os) ** 2);
    const w = t95(z) / Math.max(0.05, dauer);
    return { k: w * w, c: 2 * z * w };
  };
  const back = (t, s = 1.2) => 1 + (s + 1) * (t - 1) ** 3 + s * (t - 1) ** 2; // landet mit kleinem Federn
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Ein Attribut nur schreiben, wenn sich sein Wert ändert. Jede Änderung lässt den Browser das SVG neu zeichnen,
  // unveränderte Werte in jedem Bild kosten Zeit und lassen den Übergang ruckeln.
  const put = (el, name, v) => {
    const cache = el.zlWerte || (el.zlWerte = {});
    if (cache[name] !== v) { cache[name] = v; el.setAttribute(name, v); }
  };
  // Federn rechnen in kleinen Schritten über die echte vergangene Zeit. Fällt ein Bild aus, läuft die Bewegung
  // trotzdem in ihrer Dauer weiter, statt langsamer zu werden und dann zu springen.
  const SCHRITT = 1 / 240;
  const reduced = () => settings.reduziert || media.matches;

  // ---------- Eingaben: Scroll, Zeiger, Finger ----------
  // Weg, den die Seite gescrollt wird, ohne dass sich etwas bewegt: Ein Element mit data-zl-logo-halt steht dabei fest
  // (position: sticky, etwa die Produktbühne, deren Flaschen sich beim Scrollen drehen). Diesen Weg zählt das Logo nicht mit.
  // So bleibt oben der Schriftzug, und die Augen kommen erst, wenn sich die Seite sichtbar bewegt
  // (Moritz am iPhone, 05.10.2026: "Die Augen drehen sich weiter, wenn man noch oben ist").
  const haltWeg = () => {
    let w = 0;
    document.querySelectorAll('[data-zl-logo-halt]').forEach((el) => {
      // wie weit das Element unter seinem natürlichen Platz steht (offsetTop taugt nicht, es enthält bei sticky den Versatz schon)
      const c = el.parentElement; if (!c || !el.offsetParent) return;
      const vor = el.previousElementSibling, cs = getComputedStyle(c);
      const natur = vor ? vor.getBoundingClientRect().bottom
        : c.getBoundingClientRect().top + parseFloat(cs.borderTopWidth) + parseFloat(cs.paddingTop);
      w += Math.max(0, el.getBoundingClientRect().top - natur);
    });
    return w;
  };
  const seitenY = () => Math.max(0, window.scrollY - haltWeg());
  const input = {
    target: 0,
    lastY: window.scrollY,
    upRun: 0,
    point: null,          // letzter Zeiger- oder Fingerpunkt in Bildschirm-Pixeln
    touch: false,         // letzte Eingabe kam vom Finger
    anchor: null,         // Wischen: Punkt, der dem Finger an einer Leine hinterherläuft
    swipe: null,          // Wischen: Richtung und Stärke, Länge bis 1
    pointAt: -Infinity,   // Zeitpunkt der letzten Zeiger-Eingabe
    down: false,          // Finger liegt gerade auf
    fv: { x: 0, y: 0 },   // Tempo des Fingers in px/s, geglättet
    fAt: -Infinity,
    sv: 0,                // Tempo der Seite beim Scrollen in px/s, geglättet (läuft nach dem Loslassen mit Schwung weiter)
    sAt: -Infinity,
    sY: window.scrollY,
    downAt: -Infinity,    // Zeitpunkt des Aufsetzens
    upAt: -Infinity,      // Zeitpunkt des Loslassens
    path: 0,              // Weg des Fingers in dieser Berührung, px
    nachhall: 0,          // wie stark der Schwung nach dem Loslassen noch zählt, 0 bis 1
    ablage: 0,            // Sonderfall: wie sehr der Blick zuletzt auf dem ruhenden Finger nahe am Logo lag, 0 bis 1
    lastSwipe: null,      // Wischrichtung beim Loslassen, klingt danach mit dem Schwung ab
    sAnker: window.scrollY, // Scrollrichtung: Anker an einer Leine hinter der Scrollposition. Abstand zum Anker = Blick nach oben oder unten
  };
  // Scrollrichtung (Moritz, 05.10.2026): In Ruhe schauen die Augen geradeaus. Wer runterscrollt, dem schauen sie
  // nach unten entgegen, wer hochscrollt, nach oben. Es zählt der Weg der Seite (auch langsam und mit Schwung),
  // nicht wo der Daumen liegt, denn der liegt fast immer unten. Steht die Seite, kommt der Blick langsam zur Mitte zurück.
  // Desktop, Blickfeld Bildschirm: so nah an den Augen (px) schauen sie genau zur Maus, danach weicher Übergang
  const NAH_PX = 40;
  const NAH_UEBERGANG_PX = 60;
  const SCROLL_STILL_MS = 400;  // so lange steht die Seite, bevor der Blick zur Mitte zurückkehrt
  const RUECKKEHR_S = 0.5;      // Zeitkonstante der Rückkehr zur Mitte (nach rund 1,5 s fast ganz zurück)
  // Kurz angetippt und weggeschnippt: Die Seite läuft mit Schwung weiter, der Finger war aber kaum da.
  // Der Blick folgt dann nur so weit, wie der Finger selbst gezogen hat, und kommt schnell zur Ruhe
  // (Moritz am iPhone, 01.10.2026: "gehen die Augen schnell hoch, obwohl mein Finger nur kurz drauf ist").
  const ANLAUF_MS = 250;      // so lange braucht eine Berührung, bis ihr Tempo voll zählt
  const NACHHALL_MS = 300;    // so schnell klingt der Schwung nach dem Loslassen für die Augen ab
  // Sonderfall: Der Finger bleibt beim Hochscrollen liegen, fährt schräg hoch zum Warenkorb oder Menü und ruht dort.
  // Dann schauen die Augen zum Finger statt weiter in die alte Wischrichtung. Gilt nur, wenn er still liegt UND nahe
  // am Logo ist, beim Scrollen weiter unten ändert sich nichts (Moritz am iPhone, 02.10.2026).
  const STILL_MS = 400;       // so lange liegt der Finger still, bevor sein Ort zählt
  const STILL_BLEND_MS = 250; // und so weich wechselt der Blick dann zum Finger
  // Tempo klingt ab, wenn keine neuen Ereignisse kommen (120 ms), so kommen die Augen zur Ruhe, wenn die Seite steht
  // Empfindlichkeit am Handy: ein Regler für beides. 0 = rund 400 px Daumenweg bis an den Rand, 0,31 = rund 180 px
  // (Moritz am iPhone, 01.10.2026), 1 = rund 30 px. Das Tempo bis an den Rand ist immer das 15-Fache des Wegs pro Sekunde.
  const zugPx = () => 400 * Math.pow(0.075, clamp(settings.empfindlich, 0, 1));   // Wischweg bis zum Rand
  const schwungPx = () => 15 * zugPx();                                            // Tempo bis zum Rand in px/s
  const fade = (at) => Math.exp(-Math.max(0, now() - at - 40) / 120);
  const now = () => performance.now();

  const scrollTarget = () => {
    if (settings.immerZeichen) return 1;
    const y = seitenY();
    const dy = y - input.lastY;
    input.lastY = y;
    if (settings.modus === 'mitlaufen') return clamp(y / Math.max(1, settings.strecke), 0, 1);
    const hyst = Math.min(12, settings.schwelle / 2); // px Abstand, damit es an der Schwelle nicht flattert, auch bei kleiner Schwelle
    if (y <= settings.schwelle - hyst) { input.upRun = 0; return 0; }
    if (settings.ausfalten === 'hoch') {
      input.upRun = dy < 0 ? input.upRun - dy : 0;
      if (input.upRun > 24) return 0;          // spürbar hochgescrollt: ausfalten
      if (dy > 0 && y > settings.schwelle) return 1;
      return input.target;
    }
    if (y > settings.schwelle) return 1;
    return input.target;
  };

  const onScroll = () => {
    const t = now(), y = seitenY(), dt = t - input.sAt;
    if (dt > 0 && dt < 200) input.sv = lerp(input.sv * fade(input.sAt), ((y - input.sY) / dt) * 1000, 0.35);
    else input.sv = 0;
    input.sY = y; input.sAt = t;
    const vorher = input.target;
    input.target = scrollTarget();
    // Scrollrichtung: Der Anker läuft an einer Leine hinterher, so lang wie der Wischweg aus der Empfindlichkeit.
    // Wechselt Schriftzug und 0,0, startet der Blick in der Mitte.
    const leine = Math.max(10, zugPx()), d = y - input.sAnker;
    if (Math.abs(d) > leine) input.sAnker = y - Math.sign(d) * leine;
    if ((vorher < 0.5) !== (input.target < 0.5)) input.sAnker = y;
    wake();
  };
  const onPointer = (e) => {
    if (e.pointerType === 'touch') return; // Finger laufen über touch-Ereignisse, die auch beim Scrollen kommen
    input.touch = false;
    input.point = { x: e.clientX, y: e.clientY }; input.pointAt = now(); scheduleIdle(); wake();
  };
  // Finger: Beim Aufsetzen schauen die Augen zum Finger. Beim Wischen folgen sie der Wischrichtung:
  // ein Ankerpunkt läuft dem Finger an einer Leine hinterher, die Richtung vom Anker zum Finger ist der Blick.
  // So schauen die Augen hoch, wenn man nach oben zieht, auch wenn der Finger unter dem Logo ist.
  // Bereiche mit data-zl-logo-ruhig (die Produktbühne, in der man Flaschen seitlich wischt): Dort zählt eine Berührung erst,
  // wenn sie die Seite wirklich hoch oder runter bewegt. Seitliches Wischen und Antippen lassen die Augen in Ruhe
  // (Moritz am iPhone, 05.10.2026: "erst starten, wenn man wirklich die Seite nach unten oder oben bewegt").
  const ruhig = { an: false, x: 0, y: 0, seitlich: false, seite: 0 };
  const onTouchStart = (e) => {
    const t = e.touches[0]; if (!t) return;
    if (e.type === 'touchstart' && e.target.closest && e.target.closest('[data-zl-logo-ruhig]')) {
      Object.assign(ruhig, { an: true, x: t.clientX, y: t.clientY, seitlich: false, seite: seitenY() });
      return;
    }
    ruhig.an = false;
    input.touch = true;
    input.point = { x: t.clientX, y: t.clientY };
    input.anchor = { x: t.clientX, y: t.clientY };
    input.swipe = null;
    input.down = true; input.fv = { x: 0, y: 0 }; input.fAt = now();
    input.downAt = now(); input.path = 0; input.nachhall = 0; input.ablage = 0;
    input.pointAt = now(); scheduleIdle(); wake();
  };
  const onTouchEnd = (e) => {
    if (e.touches.length) return;
    if (ruhig.an) { ruhig.an = false; return; }
    input.down = false; input.upAt = now();
    // So weit, wie der Finger selbst gezogen hat, und nur so viel, wie die Berührung gedauert hat:
    // Nach kurzem Halten und schnellem Schnipsen schauen die Augen nicht kurz ganz hoch (Moritz am iPhone, 02.10.2026).
    const dauer = smooth(clamp((now() - input.downAt) / ANLAUF_MS, 0, 1));
    input.nachhall = clamp(input.path / Math.max(1, zugPx()), 0, 1) * dauer * (1 - input.ablage); // ruhte der Blick auf dem Finger, bleibt er dort
    input.lastSwipe = input.swipe ? { ...input.swipe } : null;
    input.pointAt = now(); scheduleIdle(); wake();
  };
  const onTouchMove = (e) => {
    const t = e.touches[0]; if (!t) return;
    if (ruhig.an) {
      if (ruhig.seitlich) return;
      const dx = t.clientX - ruhig.x, dy = t.clientY - ruhig.y;
      if (Math.hypot(dx, dy) < 8) return;                // noch keine Richtung
      if (Math.abs(dx) >= Math.abs(dy)) { ruhig.seitlich = true; return; } // seitlich: die Seite steht, die Augen auch
      if (Math.abs(seitenY() - ruhig.seite) < 2) return;  // hoch oder runter, aber die Bühne steht noch fest: weiter Ruhe
      onTouchStart({ type: 'weiter', touches: [{ clientX: ruhig.x, clientY: ruhig.y }] }); // hoch oder runter: ab hier wie überall
    }
    input.touch = true;
    const tm = now(), dt = tm - input.fAt;
    if (input.point && dt > 0 && dt < 200) {
      const k = fade(input.fAt);
      input.fv = {
        x: lerp(input.fv.x * k, ((t.clientX - input.point.x) / dt) * 1000, 0.35),
        y: lerp(input.fv.y * k, ((t.clientY - input.point.y) / dt) * 1000, 0.35),
      };
    }
    input.fAt = tm;
    if (input.point) input.path += Math.hypot(t.clientX - input.point.x, t.clientY - input.point.y);
    input.point = { x: t.clientX, y: t.clientY };
    if (!input.anchor) input.anchor = { ...input.point };
    const leash = Math.max(10, zugPx());
    let dx = t.clientX - input.anchor.x, dy = t.clientY - input.anchor.y;
    const d = Math.hypot(dx, dy);
    if (d > leash) {                                   // Anker wird mitgezogen, Blick bleibt am Rand
      input.anchor.x += dx * (1 - leash / d); input.anchor.y += dy * (1 - leash / d);
      dx = t.clientX - input.anchor.x; dy = t.clientY - input.anchor.y;
    }
    if (d > 6 || input.swipe) input.swipe = { x: dx / leash, y: dy / leash }; // erst ab ein paar Pixeln ist es ein Wischen
    input.pointAt = now(); scheduleIdle(); wake();
  };

  // ---------- Ein Logo ----------
  class Logo {
    constructor(svg) {
      this.svg = svg;
      const NS = 'http://www.w3.org/2000/svg';
      const uid = `zl-logo-${Logo.count = (Logo.count || 0) + 1}`;
      const defs = svg.querySelector('defs');
      const clip = (name) => {
        const c = document.createElementNS(NS, 'clipPath'); c.id = `${uid}-${name}`;
        const r = document.createElementNS(NS, 'rect'); r.setAttribute('y', '-12'); r.setAttribute('height', '48');
        c.append(r); defs.append(c); return { id: c.id, rect: r };
      };
      const wrap = (inner, c) => {
        const outer = document.createElementNS(NS, 'g'); outer.setAttribute('clip-path', `url(#${c.id})`);
        inner.parentNode.insertBefore(outer, inner); outer.append(inner); return outer;
      };
      // Schriftzug in zwei Blöcke: "zero" links vom Schlitz, "Labs" rechts davon
      const word = svg.querySelector('[data-zl-wort]');
      this.blocks = ['links', 'rechts'].map((side) => {
        const g = document.createElementNS(NS, 'g');
        const c = clip(side);
        const outer = document.createElementNS(NS, 'g'); outer.setAttribute('clip-path', `url(#${c.id})`);
        outer.append(g); word.append(outer);
        return { side, g, rect: c.rect };
      });
      this.letters = [...word.querySelectorAll('[data-zl-b]')].map((el, i) => {
        const bb = el.getBBox();
        const cx = bb.x + bb.width / 2;
        this.blocks[cx < SLOT ? 0 : 1].g.append(el);
        return { el, cx, box: { x: bb.x, y: bb.y, w: bb.width, h: bb.height }, i };
      });
      // Das o bekommt eine eigene Gruppe: beim Übergang "Aus dem o wird die 0,0" bleibt es stehen und wächst zur Null.
      // Bei allen anderen Übergängen läuft es mit "zero" mit, gleicher Ausschnitt, gleiche Bewegung.
      const oL = this.letters.find((l) => l.el.dataset.zlB === 'o');
      const oc = clip('o');
      const oOuter = document.createElementNS(NS, 'g'); oOuter.setAttribute('clip-path', `url(#${oc.id})`);
      const oG = document.createElementNS(NS, 'g'); oOuter.append(oG); word.append(oOuter); oG.append(oL.el);
      this.oGroup = { g: oG, rect: oc.rect, letter: oL };
      // Kohlensäure: Reihenfolge, in der die Buchstaben aufsteigen, wie Bläschen nicht von links nach rechts
      const bubble = { z: 3, e: 7, r: 1, o: 5, L: 0, a: 6, b: 2, s: 4, tm: 8 };
      this.letters.forEach((l) => { l.bubble = (bubble[l.el.dataset.zlB] ?? l.i) / 8; });
      this.sign = svg.querySelector('[data-zl-zeichen]');
      this.sign.setAttribute('opacity', '1');
      this.dot = svg.querySelector('[data-zl-punkt]');
      // Jede Null taucht nur in ihrer Hälfte des Schlitzes auf, der Punkt in der Mitte. Ausschnitte im Maßstab der 0,0.
      this.halves = {
        links: clip('fenster-links'),
        rechts: clip('fenster-rechts'),
        punkt: clip('fenster-punkt'),
      };
      wrap(svg.querySelector('[data-zl-auge="links"]'), this.halves.links);
      wrap(svg.querySelector('[data-zl-auge="rechts"]'), this.halves.rechts);
      wrap(svg.querySelector('[data-zl-punkt]'), this.halves.punkt);
      Object.values(this.halves).forEach((h) => { h.rect.setAttribute('y', '-4'); h.rect.setAttribute('height', '32'); });
      this.eyes = [...svg.querySelectorAll('[data-zl-auge]')].map((g) => {
        const pupil = g.querySelector('[data-zl-pupille]');
        const cx = parseFloat(g.dataset.cx), cy = parseFloat(g.dataset.cy);
        return { g, pupil, cx, cy, x: SIGN.rest.x, y: SIGN.rest.y, vx: 0, vy: 0, tx: SIGN.rest.x, ty: SIGN.rest.y };
      });
      this.p = input.target; this.v = 0;
      this.gaze = 0; this.spin = 0;
      this.measure();
      this.render();
    }

    // Abstand vom Schlitz zur Mitte des Kopfs, in Einheiten des Schriftzugs. Am Handy steht das Logo schon mittig, dann ist er fast 0.
    measure() {
      const rr = this.svg.getBoundingClientRect();
      if (!rr.width) { this.travel = 0; return; }
      const r = { left: rr.left - (this.shiftPx || 0), right: rr.right - (this.shiftPx || 0), width: rr.width };
      const head = this.svg.closest('header') || document.documentElement;
      const box = head.getBoundingClientRect();
      const unit = r.width / WORD_W;
      this.unit = unit;
      const slotX = r.left + SLOT * unit;
      let target = box.left + box.width / 2;
      // Wo die 0,0 anhält (Moritz, 10.10.2026): in der Mitte des Kopfs, wenn bis zum ersten Wort oder Symbol rechts
      // davon mindestens der Abstand zwischen Sektionen frei bleibt. Reicht das nicht, steht sie vor dem ersten Wort
      // wie ein weiterer Eintrag des Menüs, mit demselben Abstand wie zwischen zwei Wörtern im Kopf (gemessen, nicht
      // geschätzt). Liegt diese Stelle links vom Platz im Schriftzug, wird der Schriftzug an seinem Platz zur 0,0.
      const frei = num(rootStyle(), '--zl-space-7', 72);
      const half = (SIGN.width * settings.zeichen * unit) / 2;
      const scope = this.svg.closest('.header-wrapper') || head;
      const right = [...scope.querySelectorAll('nav a, nav button, .site-nav__link, .zlk-tablet-knopf')]
        .map((el) => (el.querySelector('.zlk-wort-text, .zl-symbol, svg') || el).getBoundingClientRect())
        .filter((b) => b.width > 0 && b.height > 0 && b.left > r.right)
        .reduce((m, b) => Math.min(m, b.left), Infinity);
      if (right < Infinity && target + half + frei > right) {
        const woerter = [...scope.querySelectorAll('nav .zlk-wort-text')]
          .map((el) => el.getBoundingClientRect())
          .filter((b) => b.width > 0 && b.left > r.right)
          .sort((a, b) => a.left - b.left);
        const luecken = woerter.slice(1).map((b, i) => b.left - woerter[i].right).filter((g) => g > 0);
        const abstand = luecken.length ? Math.min(...luecken) : frei;
        // Optisch gemessen: der Abstand gilt ab der sichtbaren Kante der rechten Null, nicht ab ihrem Kasten
        target = right - abstand - half * SIGN.tinte;
      }
      this.travel = Math.max(0, (target - slotX) / unit);
    }

    stepSpring(dt) {
      const t = input.target;
      if (reduced()) { this.p = t; this.v = 0; return false; }
      const closing = t > this.p;
      const { k, c } = closing ? spring(settings.zuDauer, settings.zuSchwingen) : spring(settings.aufDauer, settings.aufSchwingen);
      for (let rest = dt; rest > 0; rest -= SCHRITT) {
        const h = Math.min(SCHRITT, rest);
        this.v += (-k * (this.p - t) - c * this.v) * h; this.p += this.v * h;
      }
      if (Math.abs(this.p - t) < 1e-4 && Math.abs(this.v) < 1e-3) { this.p = t; this.v = 0; return false; }
      return true;
    }

    eyeTargets() {
      const t = now();
      // Zurück zum Poster-Blick nur mit der Maus. Am Handy bleibt der Blick nach dem Loslassen, wo er ist:
      // Nach ein paar Sekunden ohne Anlass nach links unten zu schauen, wirkt dort zufällig (Moritz am iPhone, 02.10.2026).
      const idle = !input.touch && settings.ruhe > 0 && t - input.pointAt > settings.ruhe * 1000;
      const tx = SIGN.travel.x * settings.blick, ty = SIGN.travel.y * settings.blick;
      for (const e of this.eyes) {
        let ox = SIGN.rest.x, oy = SIGN.rest.y;
        if (!reduced() && !idle) {
          let nx = null, ny = null;
          if (input.touch && settings.finger === 'scroll') {
            // Oben und unten: Weg der Seite seit dem Anker, runter heißt nach unten schauen
            const leine = Math.max(10, zugPx());
            ny = clamp((window.scrollY - input.sAnker) / leine, -1, 1);
            // Seitlich nur bei klar seitlichem Wischen (Bilder durchblättern). Daumenbögen beim Scrollen zählen nicht.
            const anlauf = input.down ? smooth(clamp((t - input.downAt) / ANLAUF_MS, 0, 1)) : 0;
            const sw = input.down ? input.swipe : input.lastSwipe;
            const swW = input.down ? anlauf : Math.exp(-Math.max(0, t - input.upAt) / NACHHALL_MS);
            nx = 0;
            if (sw) nx = sw.x * swW * smooth(clamp((Math.abs(sw.x) - Math.abs(sw.y)) / 0.5, 0, 1));
            // Sonderfall wie bei "Daumen und Schwung": Finger liegt still und nahe am Logo, dann schauen sie zum Finger
            const m = input.down && input.point && e.g.getScreenCTM();
            if (m) {
              const sx = m.a * e.cx + m.c * e.cy + m.e, sy = m.b * e.cx + m.d * e.cy + m.f;
              const dx = input.point.x - sx, dy = input.point.y - sy;
              const still = smooth(clamp((t - input.fAt - STILL_MS) / STILL_BLEND_MS, 0, 1));
              const nahe = 1 - smooth(clamp(Math.hypot(dx, dy) / Math.max(1, settings.reichweite), 0, 1));
              const a = still * nahe;
              const px = clamp(dx / Math.max(1, innerWidth / 2), -1, 1);
              const py = clamp(dy >= 0 ? dy / Math.max(1, innerHeight - sy) : dy / Math.max(1, sy), -1, 1);
              nx = lerp(nx, px, a); ny = lerp(ny, py, a);
            }
            const n = Math.hypot(nx, ny); if (n > 1) { nx /= n; ny /= n; }
          } else if (input.touch && settings.finger === 'natuerlich' && input.point) {
            const m = e.g.getScreenCTM();
            if (m) {
              const sx = m.a * e.cx + m.c * e.cy + m.e, sy = m.b * e.cx + m.d * e.cy + m.f;
              // Wo ist der Daumen? Seitlich über die ganze Breite, nach unten bis zum unteren Bildschirmrand.
              // Nach dem Loslassen bleibt der Blick dort, wo der Daumen zuletzt war.
              const dx = input.point.x - sx, dy = input.point.y - sy;
              const px = clamp(dx / Math.max(1, innerWidth / 2), -1, 1);
              const py = clamp(dy >= 0 ? dy / Math.max(1, innerHeight - sy) : dy / Math.max(1, sy), -1, 1);
              // Wohin bewegt sich etwas? Zwei Zeichen, das stärkere gewinnt:
              // der Weg des Fingers (auch ganz langsam gezogen) und das Tempo (beim Scrollen die Seite, auch mit Schwung).
              // Nach oben gezogen heißt hochschauen. Seitliche Bögen des Daumens zählen nur halb.
              const ref = Math.max(100, schwungPx());
              // Solange der Finger liegt, zählt das Tempo erst nach und nach voll (kurzes Antippen bewegt wenig).
              // Nach dem Loslassen zählt der Schwung der Seite nur so viel, wie der Finger gezogen hat, und klingt schnell ab.
              const anlauf = input.down ? smooth(clamp((t - input.downAt) / ANLAUF_MS, 0, 1)) : 0;
              const nachhall = input.down ? anlauf : input.nachhall * Math.exp(-Math.max(0, t - input.upAt) / NACHHALL_MS);
              const svy = -input.sv * fade(input.sAt) * nachhall;
              const fvy = input.down ? input.fv.y * fade(input.fAt) * anlauf : 0;
              const vy = Math.abs(svy) > Math.abs(fvy) ? svy : fvy;
              const vx = (input.down ? input.fv.x * fade(input.fAt) * anlauf : 0) * 0.5;
              const vl = Math.hypot(vx, vy) / ref;
              const wv = smooth(clamp((vl - 0.05) / 0.35, 0, 1));      // Tempo: ab 5 % zählt es, ab 40 % ganz
              // Der Weg des Fingers zählt wie das Tempo erst nach und nach und klingt nach dem Loslassen weich aus,
              // statt sofort wegzufallen. So springen die Augen weder beim Schnipsen hoch noch danach zurück.
              const swDir = input.down ? input.swipe : input.lastSwipe;
              const swW = input.down ? anlauf : nachhall;
              const sw = swDir ? { x: swDir.x * 0.5 * swW, y: swDir.y * swW } : { x: 0, y: 0 };
              const dl = Math.hypot(sw.x, sw.y);
              const wd = smooth(clamp((dl - 0.1) / 0.7, 0, 1));        // Weg: ab 10 % des Wischwegs zählt er, ab 80 % ganz
              let ux = 0, uy = 0, w = 0;
              if (wd >= wv && dl > 0) { ux = sw.x / dl; uy = sw.y / dl; w = wd; }
              else if (vl > 0) { const l = Math.hypot(vx, vy); ux = vx / l; uy = vy / l; w = wv; }
              // Sonderfall (siehe STILL_MS): still liegender Finger nahe am Logo, dann zählt sein Ort
              if (input.down) {
                const still = smooth(clamp((t - input.fAt - STILL_MS) / STILL_BLEND_MS, 0, 1));
                const nahe = 1 - smooth(clamp(Math.hypot(dx, dy) / Math.max(1, settings.reichweite), 0, 1));
                input.ablage = still * nahe;
                w *= 1 - input.ablage;
              }
              nx = px * (1 - w) + ux * w; ny = py * (1 - w) + uy * w;
              const n = Math.hypot(nx, ny); if (n > 1) { nx /= n; ny /= n; }
            }
          } else if (input.touch && settings.finger === 'wisch' && input.swipe) {
            nx = input.swipe.x; ny = input.swipe.y;
            const n = Math.hypot(nx, ny); if (n > 1) { nx /= n; ny /= n; }
          } else if (!input.touch && settings.maus === 'bildschirm' && input.point) {
            // Desktop (Moritz, 05.10.2026): Die 0,0 sitzt ganz oben, genau zur Maus geschaut wäre fast alles "unten".
            // Deshalb ist der Bildschirm das Blickfeld: Maus in der Mitte heißt geradeaus, am oberen Rand ganz hoch,
            // am unteren ganz runter. Seitlich gemessen von den Augen aus, damit rechts vom Logo auch rechts ist.
            const m = e.g.getScreenCTM();
            if (m) {
              const sx = m.a * e.cx + m.c * e.cy + m.e, sy = m.b * e.cx + m.d * e.cy + m.f;
              nx = clamp((input.point.x - sx) / Math.max(1, innerWidth / 2), -1, 1);
              ny = clamp((input.point.y - innerHeight / 2) / Math.max(1, innerHeight / 2), -1, 1);
              // Direkt an der 0,0 (zum Anklicken) schauen sie genau zur Maus, sonst würden sie dort nach oben rollen
              const dx = input.point.x - sx, dy = input.point.y - sy, d = Math.hypot(dx, dy);
              const nah = 1 - smooth(clamp((d - NAH_PX) / NAH_UEBERGANG_PX, 0, 1));
              if (nah > 0 && d >= 1) {
                const mag = 1 - Math.exp(-d / NAH_PX);
                nx = lerp(nx, (dx / d) * mag, nah); ny = lerp(ny, (dy / d) * mag, nah);
              }
              const n = Math.hypot(nx, ny); if (n > 1) { nx /= n; ny /= n; }
            }
          } else if (input.point) {
            const m = e.g.getScreenCTM();
            if (m) {
              const sx = m.a * e.cx + m.c * e.cy + m.e, sy = m.b * e.cx + m.d * e.cy + m.f;
              const dx = input.point.x - sx, dy = input.point.y - sy, d = Math.hypot(dx, dy);
              const mag = d < 1 ? 0 : 1 - Math.exp(-d / Math.max(1, settings.reichweite));
              if (d >= 1) { nx = (dx / d) * mag; ny = (dy / d) * mag; }
            }
          }
          if (nx !== null) { ox = nx * tx; oy = ny * ty; }
        }
        // Weg "Der Blick zieht": die Augen schauen in die Richtung, in die die 0,0 gleich läuft
        if (this.gaze > 0.001) {
          const look = (this.v >= 0 ? 1 : -1) * (this.travel < 0 ? -1 : 1); // hin zur Mitte, zurück zum Platz des Schriftzugs
          ox = lerp(ox, look * tx, this.gaze); oy = lerp(oy, 0, this.gaze);
        }
        e.tx = ox; e.ty = oy;
      }
    }

    stepEyes(dt) {
      let moving = false;
      for (const e of this.eyes) {
        // Solange nur der Schriftzug steht, sieht niemand die Augen: Sie stehen dann schon am Ziel, statt beim Erscheinen nachzuschwingen
        if (reduced() || this.p < 0.02) { e.x = e.tx; e.y = e.ty; e.vx = e.vy = 0; continue; }
        const { k, c } = spring(input.touch ? settings.augeDauerHandy : settings.augeDauer, settings.augeSchwingen);
        for (let rest = dt; rest > 0; rest -= SCHRITT) {
          const h = Math.min(SCHRITT, rest);
          e.vx += (-k * (e.x - e.tx) - c * e.vx) * h; e.x += e.vx * h;
          e.vy += (-k * (e.y - e.ty) - c * e.vy) * h; e.y += e.vy * h;
        }
        if (Math.abs(e.x - e.tx) + Math.abs(e.y - e.ty) > 1e-3 || Math.abs(e.vx) + Math.abs(e.vy) > 1e-2) moving = true;
      }
      return moving;
    }

    render() {
      const p = this.p;
      const k = settings.zeichen;
      const w = clamp(settings.weich, 0, 1);
      const ease = (t) => lerp(t, smoother(t), w);             // weicher: sanfter Anfang und sanftes Ende
      const signW = SIGN.width * k;
      const half = signW / 2;
      const D = this.travel || 0;

      // Weg zur Mitte: q ist der Fortschritt des Schlitzes, moveAll schiebt Schlitz, Buchstaben und 0,0,
      // moveSign nur die 0,0. Am Handy ist D fast 0, dann sehen alle Wege gleich aus.
      let q = p, moveAll = 0, moveSign = 0, spin = 0, gaze = 0;
      switch (settings.weg) {
        case 'vorfahren': {                                     // erst fährt der ganze Schriftzug zur Mitte, dann der Schlitz
          const a = p / 0.5;
          moveAll = D * (a < 0 ? a * 0.1 : ease(clamp(a, 0, 1))); // beim Zurückkommen federt er kurz nach
          q = p < 0 ? p : clamp((p - 0.5) / 0.5, 0, 1);
          break;
        }
        case 'rollen': {                                        // links entsteht die 0,0, dann rollen die Nullen wie Murmeln zur Mitte
          q = p / 0.45;
          const b = clamp((p - 0.45) / 0.55, 0, 1);
          const r = back(ease(b), 1.7);                         // kommt mit kleinem Federn zum Stehen
          moveSign = D * r;
          const turns = Math.sign(D) * Math.min(2, Math.round(Math.abs(D) / (Math.PI * 20.41 * k))); // abrollen, höchstens zwei Umdrehungen
          spin = turns * r;
          break;
        }
        case 'blick': {                                         // links entsteht die 0,0, schaut zur Mitte, hält kurz inne, folgt dem Blick
          q = p / 0.35;
          const b = clamp((p - 0.6) / 0.4, 0, 1);
          moveSign = D * ease(b);
          gaze = smooth(clamp((p - 0.33) / 0.12, 0, 1)) * (1 - smooth(clamp((p - 0.93) / 0.07, 0, 1)));
          break;
        }
        default: {                                              // Der Schlitz wandert: alles in einer Bewegung
          moveAll = D * ease(clamp(p, 0, 1));
        }
      }
      this.gaze = reduced() ? 0 : gaze;
      this.spin = reduced() ? 0 : spin;
      // Den Weg zur Mitte geht das ganze SVG (CSS-Verschiebung), damit die Klickfläche mitwandert.
      // Innen bleibt der Schlitz an seinem Platz. Am Handy ist der Weg fast 0.
      const shiftPx = (moveAll + moveSign) * (this.unit || 0);
      if (Math.abs(shiftPx - (this.shiftPx || 0)) > 0.01 || !this.svg.style.transform) {
        this.svg.style.transform = shiftPx ? `translateX(${shiftPx.toFixed(2)}px)` : '';
      }
      this.shiftPx = shiftPx;
      const S = SLOT;                                           // wo der Schlitz im SVG ist

      // Zwei Abschnitte: erst verschwindet der Schriftzug, dann erscheint die 0,0. "Nacheinander oder ineinander" lässt sie überlappen.
      const mode = settings.uebergang;
      const ov = mode === 'kronkorken' || mode === 'lider' ? Math.min(0.1, settings.uebergabe) : clamp(settings.uebergabe, 0, 0.8); // Kronkorken und Lider: erst weg, dann da
      const end1 = 0.5 + ov / 2, start2 = 0.5 - ov / 2;
      const t1 = q / end1;                                      // unter 0: Feder schwingt beim Ausfalten über
      const e1 = t1 < 0 ? t1 : ease(clamp(t1, 0, 1));
      const u2 = Math.max(0, (q - start2) / (1 - start2));      // über 1: Feder schwingt beim Zusammenschieben über
      const e2 = ease(clamp(u2, 0, 1));
      const sh = Math.max(0, e1);

      const [L, R] = this.blocks;
      const O = this.oGroup;
      const far = 2000;                                         // Ausschnitte reichen weit nach außen, auch auf breiten Bildschirmen
      const c = SIGN.width / 2;                                 // Mitte der 0,0 im eigenen Maßstab
      const oy = BASE_WORD - SIGN.base * k;                     // Oberkante der 0,0 im Schriftzug
      const ox = S - half;                                      // linke Kante der 0,0 im Schriftzug
      const hl = this.halves;
      const setRect = (r, x, w, y = -12, h = 48) => {
        put(r, 'x', x.toFixed(3)); put(r, 'width', Math.max(0, w).toFixed(3));
        put(r, 'y', y.toFixed(3)); put(r, 'height', Math.max(0, h).toFixed(3));
      };
      const deg = this.spin * 360;                              // Weg "Die Augen rollen": jede Null dreht sich um ihre Mitte
      const rot = (e) => (deg ? ` rotate(${deg.toFixed(2)} ${e.cx} ${e.cy})` : '');

      // Grundzustand, den die Übergänge verändern
      let lx = 0, rx = 0, lScaleY = 1;                          // Bewegung der beiden Hälften des Schriftzugs
      let lClipX = S - far, rClipX = S, clipY = -12, clipH = 48;
      let oT = null, oVisible = true, oClipX = null;
      let signScaleY = 1;
      let eyeX = [0, 0], eyeY = [0, 0], pupilR = 1, dotY = 0;
      let hx = { links: [-10, c + 10], rechts: [c, c + 10], punkt: [-10, SIGN.width + 20] };
      let hy = [-4, 32], dotHy = null, lidScale = 1;
      const letterT = new Map();                                // zusätzliche Bewegung einzelner Buchstaben

      switch (mode) {
        case 'o': {
          // "zer" gleitet von links, "Labs" von rechts ins o und verschwindet an seinen Kanten.
          // Dann wächst das o zur linken Null, die rechte Null gleitet aus ihr heraus, zuletzt die Pupillen und der Punkt.
          const ob = O.letter.box;
          const zLeft = Math.min(...this.letters.filter((l) => l.cx < ob.x).map((l) => l.box.x));
          const sRight = Math.max(...this.letters.filter((l) => l.cx > ob.x + ob.w).map((l) => l.box.x + l.box.w));
          lx = (ob.x - zLeft) * sh; rx = -(sRight - (ob.x + ob.w)) * sh;
          lClipX = ob.x - far; rClipX = ob.x + ob.w; oClipX = -far;
          const m = ease(clamp(u2 * 2, 0, 1));                  // o wird zur Null
          const n = ease(clamp(u2 * 2 - 1, 0, 1));              // zweite Null, Pupillen, Punkt
          const tw = 16.016 * k, th = 20.41 * k;
          const sx = lerp(1, tw / ob.w, m), sy = lerp(1, th / ob.h, m);
          const tx = lerp(ob.x, ox, m), ty = lerp(ob.y, oy, m);
          oT = `translate(${tx.toFixed(3)} ${ty.toFixed(3)}) scale(${sx.toFixed(4)} ${sy.toFixed(4)}) translate(${(-ob.x).toFixed(3)} ${(-ob.y).toFixed(3)})`;
          const swapped = m >= 0.999;
          oVisible = !swapped;
          hx.links = swapped ? [-10, c + 10] : [0, 0];
          hx.rechts = [16.016 + 0.2, 40];                       // rechte Null nur rechts der linken sichtbar
          eyeX = [0, -21.92 * (1 - n)];
          pupilR = ease(clamp((n - 0.25) / 0.6, 0, 1));
          const dn = ease(clamp((n - 0.55) / 0.45, 0, 1));
          hx.punkt = [SIGN.meet - 2.3 * dn, 4.6 * dn];
          break;
        }
        case 'lider': {
          // Die Buchstaben gleiten in die Mitte und verschwinden dort. Dann erscheint jede Null als flacher Strich,
          // wie ein geschlossenes Auge, und öffnet sich. Zuletzt der Punkt.
          lx = S * sh; rx = -(WORD_W - S) * sh;
          lClipX = S - far; rClipX = S;
          const visible = u2 > 0;
          hx = visible ? { links: [-10, c + 10], rechts: [c, c + 10], punkt: [-10, SIGN.width + 20] } : { links: [0, 0], rechts: [0, 0], punkt: [0, 0] };
          lidScale = u2 > 1 ? 1 + (u2 - 1) * 0.5 : 0.07 + 0.93 * e2;
          pupilR = ease(clamp((u2 - 0.35) / 0.5, 0, 1));
          const dn = ease(clamp((u2 - 0.6) / 0.4, 0, 1));
          dotHy = [21.5 - 2.3 * dn, 4.6 * dn];
          break;
        }
        case 'kronkorken': {
          // Der Schriftzug kippt weg wie ein Kronkorken, der sich dreht, auf der Rückseite erscheint die 0,0.
          lScaleY = e1 < 0 ? 1 - e1 * 0.25 : 1 - e1;           // beim Ausfalten federt er kurz in die Höhe
          signScaleY = u2 > 1 ? 1 + (u2 - 1) * 0.6 : e2;
          hx = { links: [-10, c + 10], rechts: [c, c + 10], punkt: [-10, SIGN.width + 20] };
          break;
        }
        case 'kohlensaeure': {
          // Die Buchstaben steigen einzeln nach oben weg wie Bläschen, die 0,0 steigt von unten nach.
          clipY = -0.6; clipH = 24;                             // Fenster vom oberen Rand des L bis unter den Punkt
          const spread = 0.4;
          for (const l of this.letters) {
            const ei = t1 < 0 ? t1 * 0.15 : ease(clamp((t1 - l.bubble * spread) / (1 - spread), 0, 1));
            if (Math.abs(ei) > 0.0005) letterT.set(l, `translate(0 ${(-26 * ei).toFixed(3)})`);
          }
          const ys = (clipY - oy) / k;
          hy = [ys, clipH / k];
          const v = (d) => ease(clamp((u2 - d) / 0.7, 0, 1));
          eyeY = [26 * (1 - v(0)), 26 * (1 - v(0.3))];
          dotY = 26 * (1 - v(0.15));
          break;
        }
        default: {
          // Der Schlitz: "zero" und "Labs" gleiten in einen Schlitz zwischen o und L,
          // aus dem Schlitz schieben sich die Nullen nach außen, der Punkt öffnet sich in der Mitte.
          const open = (half + 3) * e2;
          lx = S * sh; rx = -(WORD_W - S) * sh;
          lClipX = S - open - far; rClipX = S + open;
          const o = open / k;
          hx = { links: [c - o, o], rechts: [c, o], punkt: [c - o, 2 * o] };
          eyeX = this.eyes.map((e) => (e.cx < c ? 1 : -1) * c * (1 - e2));
        }
      }

      // Schriftzug
      const cyW = 9.96;                                         // Mitte der Buchstaben in der Höhe
      const flip = lScaleY !== 1 ? ` translate(0 ${cyW}) scale(1 ${lScaleY.toFixed(4)}) translate(0 ${-cyW})` : '';
      put(L.g, 'transform', `translate(${lx.toFixed(3)} 0)${flip}`);
      put(R.g, 'transform', `translate(${rx.toFixed(3)} 0)${flip}`);
      put(O.g, 'transform', oT || `translate(${lx.toFixed(3)} 0)${flip}`);
      const sichtbar = oVisible ? '' : 'hidden';
      if (O.g.style.visibility !== sichtbar) O.g.style.visibility = sichtbar;
      setRect(L.rect, lClipX, far, clipY, clipH);
      setRect(R.rect, rClipX, far, clipY, clipH);
      if (oClipX === null) setRect(O.rect, lClipX, far, clipY, clipH); else setRect(O.rect, oClipX, 2 * far, clipY, clipH);
      // Schwingt die Feder beim Ausfalten über, atmet der Schriftzug gleichmäßig, nichts reißt auseinander.
      const breath = e1 < 0 && (mode === 'schlitz' || mode === 'lider' || mode === 'o') ? -e1 * 0.06 : 0;
      for (const l of this.letters) {
        const t = letterT.get(l) || (breath ? `translate(${((l.cx - S) * breath).toFixed(3)} 0)` : '');
        put(l.el, 'transform', t);
      }

      // 0,0
      const signFlip = signScaleY !== 1 ? ` translate(0 11.77) scale(1 ${Math.max(0, signScaleY).toFixed(4)}) translate(0 -11.77)` : '';
      put(this.sign, 'transform', `translate(${ox.toFixed(3)} ${oy.toFixed(3)}) scale(${k})${signFlip}`);
      setRect(hl.links.rect, hx.links[0], hx.links[1], hy[0], hy[1]);
      setRect(hl.rechts.rect, hx.rechts[0], hx.rechts[1], hy[0], hy[1]);
      if (dotHy) setRect(hl.punkt.rect, hx.punkt[0], hx.punkt[1], dotHy[0], dotHy[1]);
      else setRect(hl.punkt.rect, hx.punkt[0], hx.punkt[1], hy[0], hy[1]);
      put(this.dot, 'transform', dotY ? `translate(0 ${dotY.toFixed(3)})` : '');
      this.eyes.forEach((e, i) => {
        const lid = lidScale !== 1 ? ` translate(0 ${e.cy}) scale(1 ${lidScale.toFixed(4)}) translate(0 ${-e.cy})` : '';
        put(e.g, 'transform', `translate(${eyeX[i].toFixed(3)} ${eyeY[i].toFixed(3)})${rot(e)}${lid}`);
        put(e.pupil, 'cx', (e.cx + e.x).toFixed(3));
        put(e.pupil, 'cy', (e.cy + e.y).toFixed(3));
        put(e.pupil, 'r', (3.936 * pupilR).toFixed(3));
      });
      const zustand = p > 0.5 ? 'zeichen' : 'schriftzug';
      if (this.svg.dataset.zlZustand !== zustand) this.svg.dataset.zlZustand = zustand;
    }
  }

  // ---------- Takt ----------
  const logos = [];
  let raf = 0, last = 0;
  const frame = (t) => {
    // echte vergangene Zeit, höchstens 0,1 s (nach einem Tab-Wechsel springt nichts)
    const dt = Math.min(0.1, Math.max(0.001, (t - last) / 1000 || 0.016));
    last = t;
    let active = false;
    // Scrollrichtung: Steht die Seite, holt der Anker die Scrollposition langsam ein, der Blick kehrt zur Mitte zurück
    let zurMitte = false;
    if (settings.finger === 'scroll' && input.touch) {
      const rest = window.scrollY - input.sAnker;
      if (Math.abs(rest) > 0.5) {
        zurMitte = true;
        if (now() - input.sAt > SCROLL_STILL_MS) input.sAnker += rest * (1 - Math.exp(-dt / RUECKKEHR_S));
      } else input.sAnker = window.scrollY;
    }
    for (const l of logos) {
      if (l.stepSpring(dt)) active = true;
      l.eyeTargets();
      if (l.stepEyes(dt)) active = true;
      l.render();
    }
    const settling = now() - Math.max(input.sAt, input.fAt) < 800; // Schwung klingt noch ab
    raf = active || zurMitte || (input.touch && settling) ? requestAnimationFrame(frame) : 0;
  };
  // Nach der Ruhezeit einmal wecken, damit die Augen zum Poster-Blick zurückkehren
  let idleTimer = 0;
  const scheduleIdle = () => {
    clearTimeout(idleTimer);
    if (settings.ruhe > 0) idleTimer = setTimeout(wake, settings.ruhe * 1000 + 30);
  };
  function wake() {
    if (!raf) { last = performance.now(); raf = requestAnimationFrame(frame); }
  }

  const refresh = () => { logos.forEach((l) => l.measure()); input.target = scrollTarget(); wake(); };
  // Neue Logos auf der Seite finden. Logos, die nicht mehr auf der Seite sind (Theme-Editor lädt den Kopf neu), fallen raus.
  const scan = () => {
    for (let i = logos.length - 1; i >= 0; i--) if (!logos[i].svg.isConnected) logos.splice(i, 1);
    document.querySelectorAll('svg[data-zl-logo]').forEach((svg) => {
      if (svg.zlLogo) return;
      svg.zlLogo = new Logo(svg);
      logos.push(svg.zlLogo);
    });
    input.target = scrollTarget();
    logos.forEach((l) => { l.p = input.target; l.render(); });
    wake();
  };
  const init = () => {
    scan();
    document.addEventListener('shopify:section:load', scan);   // Theme-Editor
    window.addEventListener('scroll', onScroll, { passive: true });
    const remeasure = () => { logos.forEach((l) => { l.measure(); l.render(); }); };
    window.addEventListener('resize', remeasure, { passive: true });
    window.addEventListener('load', remeasure);                 // Schrift und Kopf des Themes stehen erst nach dem Laden fest
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('pointerdown', onPointer, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    media.addEventListener && media.addEventListener('change', refresh);
    wake();
  };

  window.zlLogo = { settings, defaults, refresh, logos };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
